import * as types from 'features/company/companyActions';
import { REHYDRATE, RehydrateAction } from 'redux-persist';
import { Company, FeatureKey, FeaturePermission, ReduxState, Status, TelematicProviderTypeMetadata } from 'types';
import { STATUS_INITIAL, STATUS_LOADING, STATUS_SUCCEEDED } from 'myParkdUtils/constants';


export enum FormSource {
    UPDATE_COMPANY_DETAILS = 'UPDATE_COMPANY_DETAILS',
    UPDATE_WORKING_HOURS = 'UPDATE_WORKING_HOURS',
    REMOVE_WORKING_HOURS = 'REMOVE_WORKING_HOURS',
    UPDATE_DRIVER_SETTINGS = 'UPDATE_DRIVER_SETTINGS'
}

type UpdateStatus = Status & { formSource: FormSource | null };

export type CompanyDetailsState = {
    companyDetails: Company;
    companyCreateStatus: Status;
    companyGetStatus: Status;
    companyUpdateStatus: UpdateStatus;
    telematicTypeMetadata: TelematicProviderTypeMetadata[],
    telematicTypeMetadataStatus: Status,
    featurePermissions: FeaturePermission[]
};

export const emptyCompany = {
    id: '',
    name: '',
    vat: '',
    address: {
        street: '',
        number: '',
        city: '',
        postalCode: '',
        countryCode: '',
    },
    email: '',
    onboardedOn: null,
    onboardingPage: 'DEFAULT',
} as unknown as Company;

export const defaultState: CompanyDetailsState = {
    companyDetails: emptyCompany,
    companyCreateStatus: {
        loading: false,
        succeeded: false,
        error: null,
    },
    companyGetStatus: {
        loading: false,
        succeeded: false,
        error: null,
    },
    companyUpdateStatus: {
        loading: false,
        succeeded: false,
        error: null,
        formSource: null
    },
    telematicTypeMetadata: [],
    telematicTypeMetadataStatus: STATUS_INITIAL,
    featurePermissions: []
};

export default function companyDetails(state: CompanyDetailsState = defaultState, action: types.CompanyAction | RehydrateAction) {

    switch (action.type) {

        case types.COMPANY_CREATE_START:
            return {
                ...state,
                companyCreateStatus: {
                    loading: true,
                    succeeded: false,
                    error: null,
                },
            };

        case types.COMPANY_CREATE_COMPLETED:
            return {
                ...state,
                companyCreateStatus: {
                    loading: false,
                    succeeded: true,
                    error: null,
                },
            };

        case types.COMPANY_CREATE_ERROR:
            return {
                ...state,
                companyCreateStatus: {
                    loading: false,
                    succeeded: false,
                    error: action.error,
                },
            };

        //-------------------------------------------------------------------

        case types.COMPANY_REQUEST_START:
            return {
                ...state,
                companyGetStatus: {
                    loading: true,
                    succeeded: false,
                    error: null,
                },
            };

        case types.COMPANY_REQUEST_COMPLETED:
            return {
                ...state,
                companyDetails: action.payload.company,
                companyGetStatus: {
                    loading: false,
                    succeeded: true,
                    error: null,
                },
            };

        case types.COMPANY_REQUEST_ERROR:
            return {
                ...state,
                companyGetStatus: {
                    loading: false,
                    succeeded: false,
                    error: action.error,
                },
            };

        //-------------------------------------------------------------------

        case types.COMPANY_UPDATE_START:
            return {
                ...state,
                companyUpdateStatus: {
                    loading: true,
                    succeeded: false,
                    error: null,
                    formSource: null
                },
            };

        case types.COMPANY_UPDATE_COMPLETED:
            return {
                ...state,
                companyDetails: {
                    ...state.companyDetails,
                    ...action.payload.companyDetails
                },
                companyUpdateStatus: {
                    loading: false,
                    succeeded: true,
                    error: null,
                    formSource: action.payload.formSource
                },
            };

        case types.COMPANY_UPDATE_ERROR:
            return {
                ...state,
                companyUpdateStatus: {
                    loading: false,
                    succeeded: false,
                    error: action.error,
                    formSource: action.payload.formSource
                },
            };

        //-------------------------------------------------------------------

        case types.REQUEST_TELEMATIC_ACCOUNT_TYPES_START:
            return {
                ...state,
                telematicTypeMetadataStatus: STATUS_LOADING
            }

        case types.REQUEST_TELEMATIC_ACCOUNT_TYPES_COMPLETED:
            return {
                ...state,
                telematicTypeMetadata: action.payload.telematicProviderTypeMetadata,
                telematicTypeMetadataStatus: STATUS_SUCCEEDED
            }

        case types.REQUEST_TELEMATIC_ACCOUNT_TYPES_ERROR:
            return {
                ...state,
                telematicTypeMetadataStatus: {
                    loading: false,
                    succeeded: false,
                    error: action.error
                }
            }

        //-------------------------------------------------------------------

        case types.COMPANY_REQUEST_FEATURE_PERMISSIONS:
            return {
                ...state
            }

        case types.COMPANY_REQUEST_FEATURE_PERMISSIONS_COMPLETED:
            return {
                ...state,
                featurePermissions: action.payload.featurePermissions
            }

        case types.COMPANY_REQUEST_FEATURE_PERMISSIONS_ERROR:
            return {
                ...state
            }

        //-------------------------------------------------------------------

        case REHYDRATE:
            let companyDetails = action.payload && action.payload.companyDetails && action.payload.companyDetails.companyDetails; // IE
            if (companyDetails) {
                return {
                    ...defaultState,
                    companyDetails: companyDetails,
                };
            }
            return { ...state };

        default:
            return state;
    }
}

export const getCompany = (state: ReduxState): Company => state.companyDetails?.companyDetails;
export const getCompanyPublicId = (state: ReduxState): string => state.companyDetails?.companyDetails?.publicId;
export const getAllTelematicTypeMetadata = (state: ReduxState): TelematicProviderTypeMetadata[] => {
    return [...state.companyDetails.telematicTypeMetadata].sort((a, b) => {
        if (a.displayName < b.displayName) {
            return -1;
        }
        if (a.displayName > b.displayName) {
            return 1;
        }
        return 0; // equal
    })
}

export const isFeatureEnabled = (featurePermissions: FeaturePermission[], featureKey: FeatureKey): boolean =>
    featurePermissions.some((feature) => feature.featureKey === featureKey);
